/**
 * Sparkline — a smooth area+line mini chart (DS core). Generates a Catmull-Rom
 * smoothed path from `data`, fills a token-coloured gradient, draws faint
 * gridlines, an optional highlighted point (dot + guide), and redraws on hover
 * of the nearest `.group` ancestor. Content-agnostic: pass the values + colour.
 */
type Props = {
  /** y values; higher = higher on the chart. */
  data: number[];
  /** index of the point to highlight (dot + vertical guide). */
  highlight?: number;
  /** line/fill colour (token var). */
  color?: string;
  className?: string;
};

const W = 240;
const H = 84;
const PAD = 10;

function smoothPath(xs: number[], ys: number[]) {
  let d = `M${xs[0].toFixed(1)},${ys[0].toFixed(1)}`;
  for (let i = 0; i < xs.length - 1; i++) {
    const x0 = xs[i - 1] ?? xs[i], y0 = ys[i - 1] ?? ys[i];
    const x1 = xs[i], y1 = ys[i];
    const x2 = xs[i + 1], y2 = ys[i + 1];
    const x3 = xs[i + 2] ?? x2, y3 = ys[i + 2] ?? y2;
    const c1x = x1 + (x2 - x0) / 6, c1y = y1 + (y2 - y0) / 6;
    const c2x = x2 - (x3 - x1) / 6, c2y = y2 - (y3 - y1) / 6;
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)}`;
  }
  return d;
}

export function Sparkline({ data, highlight, color = "var(--ds-accent)", className = "" }: Props) {
  const max = Math.max(...data), min = Math.min(...data);
  const span = max - min || 1;
  const xs = data.map((_, i) => (i / (data.length - 1)) * W);
  const ys = data.map((v) => H - PAD - ((v - min) / span) * (H - 2 * PAD));
  const line = smoothPath(xs, ys);
  const area = `${line} L${W},${H} L0,${H} Z`;
  const hx = highlight != null ? xs[highlight] : null;
  const hy = highlight != null ? ys[highlight] : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={`w-full ${className}`} fill="none" aria-hidden>
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.34" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <filter id="sparkGlow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="3" /></filter>
      </defs>
      <g stroke="var(--ds-ink)" strokeOpacity="0.06" strokeWidth="1">
        <line x1="0" y1="22" x2={W} y2="22" /><line x1="0" y1="46" x2={W} y2="46" /><line x1="0" y1="70" x2={W} y2="70" />
      </g>
      <path d={area} fill="url(#sparkFill)" />
      <path className="ds-chart-line" d={line} stroke={color} strokeWidth="3" strokeLinecap="round" />
      {hx != null && hy != null && (
        <>
          <line x1={hx} y1={hy} x2={hx} y2={H} stroke={color} strokeOpacity="0.28" strokeWidth="1.5" strokeDasharray="3 3" />
          <circle cx={hx} cy={hy} r="5" fill={color} opacity="0.5" filter="url(#sparkGlow)" />
          <circle cx={hx} cy={hy} r="4.5" fill={color} stroke="white" strokeWidth="2" />
        </>
      )}
    </svg>
  );
}
